import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  CardBody,
  Row,
  Col,
  Card,
  CardHeader,
  Spinner,
  Input,
  Badge,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';

import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import BreadCrumb from '../../../Components/Common/BreadCrumb';

const ActivityLogView = ({ module }) => {
  const token = getAuthToken();
  const { user } = useSelector(s => s.Login);
  const roles = user?.role?.split(',').map(r => r.trim()) || [];
  const isSuperAdmin = roles.includes('Super Admin');

  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userNameFilter, setUserNameFilter] = useState('');
  const [debouncedUserName, setDebouncedUserName] = useState('');

  const [sortField, setSortField] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserName(userNameFilter), 500);
    return () => clearTimeout(timer);
  }, [userNameFilter]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const qs = new URLSearchParams();
      if (module) {
        qs.append('Module', module);
      }
      qs.append('PageNumber', currentPage);
      qs.append('PageSize', rowsPerPage);
      qs.append('SortBy', sortField);
      qs.append('IsDescending', sortDirection === 'desc');

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }
      if (dateFrom) {
        qs.append('DateFrom', dateFrom);
      }
      if (dateTo) {
        qs.append('DateTo', dateTo);
      }
      if (
        isSuperAdmin &&
        debouncedUserName &&
        debouncedUserName.trim() !== ''
      ) {
        qs.append('UserName', debouncedUserName.trim());
      }

      const response = await fetch(
        `${api.API_URL}/activity-log?${qs.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 403) {
        toast.error('Anda tidak memiliki akses ke halaman ini.');
        setData([]);
        setTotalRows(0);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success === false) {
        toast.error(result.message || 'Gagal memuat data log aktivitas.');
        setData([]);
        setTotalRows(0);
        return;
      }

      setData(result.data?.items || []);
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      toast.error(`Gagal memuat data: ${error.message}`);
      setData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    module,
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    dateFrom,
    dateTo,
    debouncedUserName,
    sortField,
    sortDirection,
    token,
    isSuperAdmin,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = rows => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    setSortField(column.id);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleFilterChange = setter => e => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = dateStr => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      id: 'createdDate',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Waktu
        </span>
      ),
      selector: row => row.createdDate,
      sortable: true,
      cell: row => <span>{formatDate(row.createdDate)}</span>,
      width: '180px',
    },
    {
      id: 'userName',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          User
        </span>
      ),
      selector: row => row.userName,
      sortable: true,
      cell: row => <span>{row.userName || '-'}</span>,
    },
    {
      id: 'action',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Action
        </span>
      ),
      selector: row => row.action,
      sortable: true,
      cell: row => (
        <Badge color='info' className='badge-outline'>
          {row.action || '-'}
        </Badge>
      ),
    },
    {
      id: 'description',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>
          Deskripsi
        </span>
      ),
      selector: row => row.description,
      sortable: true,
      cell: row => <span className='text-muted'>{row.description || '-'}</span>,
      grow: 2,
    },
    {
      id: 'referenceNo',
      name: (
        <span className='text-uppercase fw-semibold fs-12 text-muted'>Ref</span>
      ),
      selector: row => row.referenceNo,
      sortable: true,
      cell: row => <span>{row.referenceNo || '-'}</span>,
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb
            title={module ? `Log Aktivitas — ${module}` : 'Log Aktivitas'}
            pageTitle='Activity Log'
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className='g-3 align-items-end'>
                    <Col md={3}>
                      <label className='form-label fw-semibold'>Search</label>
                      <Input
                        type='text'
                        placeholder='Cari...'
                        value={searchText}
                        onChange={handleFilterChange(setSearchText)}
                      />
                    </Col>
                    <Col md={2}>
                      <label className='form-label fw-semibold'>
                        Dari Tanggal
                      </label>
                      <Input
                        type='date'
                        value={dateFrom}
                        onChange={handleFilterChange(setDateFrom)}
                      />
                    </Col>
                    <Col md={2}>
                      <label className='form-label fw-semibold'>
                        Sampai Tanggal
                      </label>
                      <Input
                        type='date'
                        value={dateTo}
                        onChange={handleFilterChange(setDateTo)}
                      />
                    </Col>
                    {isSuperAdmin && (
                      <Col md={3}>
                        <label className='form-label fw-semibold'>User</label>
                        <Input
                          type='text'
                          placeholder='Filter by user...'
                          value={userNameFilter}
                          onChange={handleFilterChange(setUserNameFilter)}
                        />
                      </Col>
                    )}
                  </Row>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    sortServer
                    highlightOnHover
                    responsive
                    striped
                    progressPending={isLoading}
                    progressComponent={
                      <Spinner color='primary' className='my-4' />
                    }
                    noDataComponent={
                      <p className='my-4 text-muted text-center'>
                        Tidak ada data.
                      </p>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default ActivityLogView;
